import { vec2 } from "gl-matrix";
import Sprite from "../sprite";
import { Directions, TileSet, Tile, TileMap, TileLayer } from "../interfaces";
import { makeMoveCommand } from "./input-helpers";

import { state } from "../index";

export function walkableNeighborsForTileAtCoordinates(
  x: number,
  y: number,
  tileLayer: TileLayer,
  tileSet: TileSet
): vec2[] {
  const width = tileLayer.width;
  const height = tileLayer.height;
  const tileX = xCoordinateForTileFromXWorldCoordinate(x);
  const tileY = yCoordinateForTileFromYWorldCoordinate(y);
  const neighbors = [];

  if (tileX > 0) {
    const leftX = x - 1;
    const leftY = y;
    const left = coordinatesCanBeReachedFromPosition(x, y, leftX, leftY, tileLayer, tileSet);
    if (left) neighbors.push([leftX, leftY]);
  }

  if (tileX < width - 1) {
    const rightX = x + 1;
    const rightY = y;
    const right = coordinatesCanBeReachedFromPosition(x, y, rightX, rightY, tileLayer, tileSet);
    if (right) neighbors.push([rightX, rightY]);
  }

  if (tileY > 0) {
    const upX = x;
    const upY = y + 1;
    const up = coordinatesCanBeReachedFromPosition(x, y, upX, upY, tileLayer, tileSet);
    if (up) neighbors.push([upX, upY]);
  }

  if (tileY < height - 1) {
    const downX = x;
    const downY = y - 1;
    const down = coordinatesCanBeReachedFromPosition(x, y, downX, downY, tileLayer, tileSet);
    if (down) neighbors.push([downX, downY]);
  }

  return neighbors;
}

export function coordinatesCanBeReachedFromPosition(
  toX: number,
  toY: number,
  fromX: number,
  fromY: number,
  tileLayer: TileLayer,
  tileSet: TileSet
) {
  const fromTile = tileFromCoordinates(fromX, fromY, tileLayer, tileSet);
  const toTile = tileFromCoordinates(toX, toY, tileLayer, tileSet);
  if (!toTile) return false;

  const bothWalkable = isWalkable(fromTile) && isWalkable(toTile);
  const sameLevel = isSameLevelOffGround(fromTile, toTile);

  // Can be reached if both tiles are walkable and at the same height
  // e.g. don't have to go up stairs to get to the tile
  if (bothWalkable && sameLevel) return true;

  // One (or both) of the tiles isn't walkable by one of the tiles is climbable
  // e.g. stairs
  const destinationIsClimbableOrWalkable = isClimbable(toTile) || isWalkable(toTile);
  if (!bothWalkable && destinationIsClimbableOrWalkable) return true;
  return false;
}

export function xCoordinateForTileFromXWorldCoordinate(x: number) {
  if (x < 0) console.warn(`x is less than 0, so clicking out of bounds...`);
  return Math.floor(x); // Fudge is to move the point to the feet
}

export function yCoordinateForTileFromYWorldCoordinate(y: number) {
  if (y > 0) console.warn(`y is greater than 0, so clicking out of bounds...`);
  return Math.floor(Math.abs(y)); // Fudge is to move the point to the feet
}

export function tileIndexFromCoordinates(x: number, y: number, tileLayer: TileLayer) {
  const iFromWidth = xCoordinateForTileFromXWorldCoordinate(x);
  const iFromHeight = yCoordinateForTileFromYWorldCoordinate(y) * tileLayer.width;
  return iFromHeight + iFromWidth;
}

export function tileFromCoordinates(x, y, tileLayer: TileLayer, tileSet: TileSet) {
  // subtracting by the firstgid is weird, but... I guess I have to?
  // https://doc.mapeditor.org/en/stable/reference/json-map-format/
  const index = tileIndexFromCoordinates(x, y, tileLayer);
  return tileFromTileIndex(index, tileLayer, tileSet);
}

function tileFromTileIndex(index: number, tileLayer: TileLayer, tileSet: TileSet) {
  const tileIndex = tileLayer.data[index];
  if (!tileIndex) return null;
  return tileSet.tiles[tileIndex - tileSet.firstgid];
}

export function isSameLevelOffGround(tile1: Tile, tile2: Tile) {
  return levelOffGround(tile1) === levelOffGround(tile2);
}

function levelOffGround(tile: Tile) {
  return propValueFromName(tile, "levelOffGround");
}

export function isReachable(tile: Tile) {
  return isWalkable(tile) || isClimbable(tile);
}

export function isWalkable(tile: Tile) {
  return propValueFromName(tile, "walkable");
}

export function isClimbable(tile: Tile) {
  return propValueFromName(tile, "climbable");
}

function propValueFromName(tile: Tile, propName: string) {
  if (!tile) {
    console.warn(`No tile when checking prop value ${propName}...`);
    return;
  }

  const prop = tile.properties.find(prop => prop.name === propName);
  if (!prop) console.warn(`No prop on tile ${tile}...`);
  return prop ? prop.value : null;
}

export function outOfBounds(x: number, y: number, tileMap: TileMap) {
  return x < 0 || x > tileMap.width || y > 0 || Math.abs(y) > tileMap.height;
}

export function moveCharacterAlongPath(character: Sprite, path, callback?: () => void) {
  if (path.length < 1) return;
  const next = path[0];
  const pX = character.pos[0];

  // an attempt to work with positionIsUnReachable in Sprite to keep player's butts out of the floor
  // const pY = character.pos[1] - character.size[1] / 2;
  const pY = character.pos[1];
  const nX = next[0];
  const nY = next[1];

  const closeEnoughX = closeEnough(pX, nX);
  const closeEnoughY = closeEnough(pY, nY);

  if (closeEnoughX && closeEnoughY) {
    path.shift();
    if (path.length === 0) {
      character.switchToIdleAnimation();
      if (callback) callback();
    }
    return;
  }

  if (pX > next[0] && !closeEnoughX) {
    makeMoveCommand(character, Directions.Left, state)();
  } else if (pX < next[0] && !closeEnoughX) {
    makeMoveCommand(character, Directions.Right, state)();
  } else if (pY > next[1] && !closeEnoughY) {
    makeMoveCommand(character, Directions.Down, state)();
  } else if (pY < next[1] && !closeEnoughY) {
    makeMoveCommand(character, Directions.Up, state)();
  }
}

function closeEnough(num1, num2) {
  const epsilon = Sprite.MOVE_AMOUNT;
  return Math.abs(num1 - num2) <= epsilon;
}
